import React, { useState, useEffect, useCallback } from 'react';
import { db, storage } from '../firebase/config.js';
import { collection, getDocs, addDoc, Timestamp, query, where, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { startOfDay, parseISO, format, differenceInDays } from 'date-fns';
import { getAuth } from "firebase/auth";
import { useNavigate, useLocation } from 'react-router-dom';
import TenantConfigService from '../services/TenantConfigService.js';
import { getScheduleTypeForDate, isSchoolHoliday, getPeriodTimesFromSchedule } from '../utils/scheduleUtils.js';

const auth = getAuth();
const RequestSub = (props) => {
  const { currentUser } = props;
  const navigate = useNavigate();
  const location = useLocation();

  const canSubmitForOthers = currentUser?.hasElevatedPrivileges || currentUser?.isSupervisor || false;

  // Define the initial state to easily reset the form
  const initialFormState = {
    department: '', name: '', date: '', reason: '',
    periods: [], room: '', periodNotes: {}, periodRooms: {}
  };

  const [formData, setFormData] = useState(initialFormState);
  const [allFacultyForTenant, setAllFacultyForTenant] = useState([]);
  
  // State variables for form submission status and feedback
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [periodFiles, setPeriodFiles] = useState({});
  const [additionalPeriodFiles, setAdditionalPeriodFiles] = useState({});
  const [isSubmittingForOther, setIsSubmittingForOther] = useState(false);
  const [selectedTeacherNameForAdmin, setSelectedTeacherNameForAdmin] = useState('');
  const [isLoadingFaculty, setIsLoadingFaculty] = useState(false);

  // Multiple rooms functionality
  const [useMultipleRooms, setUseMultipleRooms] = useState(false);
  const [periodRooms, setPeriodRooms] = useState({});

  // Multi-day request functionality
  const [requestMode, setRequestMode] = useState('single');
  const [selectedDates, setSelectedDates] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateSpecificSettings, setDateSpecificSettings] = useState({});
  const [copySettingsToAll, setCopySettingsToAll] = useState(true);

  // Configuration state
  const [tenantConfig, setTenantConfig] = useState({
    periods: [],
    customFields: [],
    academicDepartments: [],
    schoolDepartments: [],
    enabledModules: [],
    subPayEnabled: false,
    scheduleTypes: [],
    calendarAssignments: { patterns: [], specificDates: [] },
    schoolHolidays: []
  });
  
  const [divisions, setDivisions] = useState([]);

  // Filtered schedule types based on user's division
  const [availableScheduleTypes, setAvailableScheduleTypes] = useState([]);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Dynamic periods state
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [selectedDateScheduleInfo, setSelectedDateScheduleInfo] = useState(null);

  // Sharing state
  const [sharingOptions, setSharingOptions] = useState({
    shareWithDepartments: [],
    shareWithPreferredSubs: false,
    shareWithAllFaculty: false,
    selectedPreferredSubs: [],
    newGroupName: '',
    saveAsGroup: false
  });
  const [savedGroups, setSavedGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  // Create useCallback wrapper for the schedule utility function with division filtering
const getScheduleTypeForDateCallback = useCallback((dateString) => {
  // Create a modified config with only the user's available schedule types
  const divisionFilteredConfig = {
    ...tenantConfig,
    scheduleTypes: availableScheduleTypes
  };
  return getScheduleTypeForDate(dateString, divisionFilteredConfig);
}, [tenantConfig, availableScheduleTypes]);

  // Helper function to generate date range (excluding holidays)
  const generateDateRange = useCallback((start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      const dateString = date.toISOString().split('T')[0];
      const dayOfWeek = date.getDay();
      const holidayCheck = isSchoolHoliday(dateString, tenantConfig.schoolHolidays);
      
      // Skip weekends and holidays
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayCheck.isHoliday) {
        dates.push(dateString);
      }
    }
    return dates;
  }, [tenantConfig.schoolHolidays]);

  // Helper function to find supervisor for a department
const findSupervisorForDepartment = (department) => {
  if (!department || !allFacultyForTenant) return null;
  
  // Look for department head/chair first
  const deptHead = allFacultyForTenant.find(f => 
    f.department === department && 
    (f.role === 'Department Head' || 
     f.role === 'Department Chair' || 
     f.role === 'Supervisor' ||
     f.isDepartmentHead === true)
  );
  
  if (deptHead && deptHead.email) {
    return { name: deptHead.name, email: deptHead.email };
  }
  
  return null;
};

  // Effect to handle pre-filled data from absence request
  useEffect(() => {
    if (location.state?.prefillData) {
      const { prefillData } = location.state;
      console.log('Pre-filling substitute request with data:', prefillData);
      
      // Set basic form data
      if (prefillData.reason) {
        setFormData(prev => ({ ...prev, reason: prefillData.reason }));
      }
      
      if (prefillData.department) {
        setFormData(prev => ({ ...prev, department: prefillData.department }));
      }
      
      if (prefillData.name) {
        setFormData(prev => ({ ...prev, name: prefillData.name }));
      }
      
      // Handle dates
      if (prefillData.dates && prefillData.dates.length > 0) {
        if (prefillData.dates.length === 1 || 
            (prefillData.dates.length === 2 && prefillData.dates[0] === prefillData.dates[1])) {
          // Single date
          setRequestMode('single');
          setFormData(prev => ({ ...prev, date: prefillData.dates[0] }));
        } else if (prefillData.dates.length === 2) {
          // Date range
          setRequestMode('multiple');
          setStartDate(prefillData.dates[0]);
          setEndDate(prefillData.dates[1]);
          
          // Generate the date range (excluding weekends and holidays)
          const dates = generateDateRange(prefillData.dates[0], prefillData.dates[1]);
          setSelectedDates(dates);
          
          // Initialize date-specific settings for each date
          const newDateSettings = {};
          dates.forEach(date => {
            const scheduleInfo = getScheduleTypeForDateCallback(date);
            newDateSettings[date] = {
              scheduleInfo,
              availablePeriods: scheduleInfo?.scheduleType?.periods || [],
              periods: [],
              periodNotes: {},
              periodRooms: {},
              room: '',
              reason: prefillData.reason || ''
            };
          });
          setDateSpecificSettings(newDateSettings);
        }
      }
      
      // Show success message about pre-fill
      setSuccessMessage('Form pre-filled with data from your absence request. Please select periods and add any additional details.');
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, getScheduleTypeForDateCallback, generateDateRange]);

  // Handle date range selection (excluding holidays)
  const handleDateRangeChange = () => {
    if (startDate && endDate) {
      const dates = generateDateRange(startDate, endDate);
      setSelectedDates(dates);
      
      // Initialize date-specific settings for each date
      const newDateSettings = {};
      dates.forEach(date => {
        const scheduleInfo = getScheduleTypeForDateCallback(date);
        newDateSettings[date] = {
          scheduleInfo,
          availablePeriods: scheduleInfo?.scheduleType?.periods || [],
          periods: copySettingsToAll ? formData.periods : [],
          periodNotes: copySettingsToAll ? { ...formData.periodNotes } : {},
          periodRooms: copySettingsToAll ? { ...formData.periodRooms } : {},
          room: copySettingsToAll ? formData.room : '',
          reason: copySettingsToAll ? formData.reason : ''
        };
      });
      setDateSpecificSettings(newDateSettings);
    }
  };

  // Handle individual date selection (checkbox style) - excluding holidays
  const handleIndividualDateChange = (date, checked) => {
    if (checked) {
      const newDates = [...selectedDates, date].sort();
      setSelectedDates(newDates);
      
      // Initialize settings for this date
      const scheduleInfo = getScheduleTypeForDateCallback(date);
      setDateSpecificSettings(prev => ({
        ...prev,
        [date]: {
          scheduleInfo,
          availablePeriods: scheduleInfo?.scheduleType?.periods || [],
          periods: copySettingsToAll ? formData.periods : [],
          periodNotes: copySettingsToAll ? { ...formData.periodNotes } : {},
          periodRooms: copySettingsToAll ? { ...formData.periodRooms } : {},
          room: copySettingsToAll ? formData.room : '',
          reason: copySettingsToAll ? formData.reason : ''
        }
      }));
    } else {
      setSelectedDates(selectedDates.filter(d => d !== date));
      setDateSpecificSettings(prev => {
        const updated = { ...prev };
        delete updated[date];
        return updated;
      });
    }
  };

  // Update settings for a specific date
  const updateDateSpecificSetting = (date, field, value) => {
    setDateSpecificSettings(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [field]: value
      }
    }));
  };

  // Copy current form settings to all selected dates
  const copyCurrentSettingsToAllDates = () => {
    const updatedSettings = {};
    selectedDates.forEach(date => {
      updatedSettings[date] = {
        ...dateSpecificSettings[date],
        periods: [...formData.periods],
        periodNotes: { ...formData.periodNotes },
        periodRooms: { ...formData.periodRooms },
        room: formData.room,
        reason: formData.reason
      };
    });
    setDateSpecificSettings(updatedSettings);
  };

  // Update available periods when date changes
  useEffect(() => {
    console.log('=== DEBUG: useEffect for period update ===');
    console.log('formData.date:', formData.date);
    console.log('tenantConfig.scheduleTypes:', tenantConfig.scheduleTypes);
    
    // DEBUG: Check for timezone date parsing issues
    if (formData.date) {
      console.log('=== TIMEZONE DEBUG ===');
      console.log('Selected date string:', formData.date);
      
      const testDate1 = new Date(formData.date);
      const testDate2 = new Date(formData.date + 'T12:00:00');
      
      console.log('Parsed without time:', testDate1.toString(), '- Day of week:', testDate1.getDay());
      console.log('Parsed with noon time:', testDate2.toString(), '- Day of week:', testDate2.getDay());
      console.log('Expected: 0=Sunday, 1=Monday, 2=Tuesday, etc.');
      console.log('==================');
    }
    
    if (requestMode === 'single') {
      if (!formData.date || !tenantConfig.scheduleTypes) {
        console.log('Missing date or schedule types, clearing periods');
        setAvailablePeriods([]);
        setSelectedDateScheduleInfo(null);
        return;
      }

      const scheduleInfo = getScheduleTypeForDateCallback(formData.date);
      console.log('Schedule info returned:', scheduleInfo);
      setSelectedDateScheduleInfo(scheduleInfo);

      if (scheduleInfo?.scheduleType) {
        console.log('Setting available periods:', scheduleInfo.scheduleType.periods);
        setAvailablePeriods(scheduleInfo.scheduleType.periods || []);
      } else {
        console.log('No schedule type found (holiday/weekend), clearing periods');
        setAvailablePeriods([]);
      }

      // Clear selected periods if they're no longer available
      if (scheduleInfo?.scheduleType) {
        const validPeriodIds = scheduleInfo.scheduleType.periods?.map(p => p.id) || [];
        console.log('Valid period IDs for this date:', validPeriodIds);
        setFormData(prev => ({
          ...prev,
          periods: prev.periods.filter(p => validPeriodIds.includes(p)),
          periodNotes: Object.fromEntries(
            Object.entries(prev.periodNotes).filter(([periodId]) => validPeriodIds.includes(periodId))
          ),
          periodRooms: Object.fromEntries(
            Object.entries(prev.periodRooms).filter(([periodId]) => validPeriodIds.includes(periodId))
          )
        }));

        // Clear files for invalid periods
        setPeriodFiles(prev => 
          Object.fromEntries(
            Object.entries(prev).filter(([periodId]) => validPeriodIds.includes(periodId))
          )
        );
        setAdditionalPeriodFiles(prev => 
          Object.fromEntries(
            Object.entries(prev).filter(([periodId]) => validPeriodIds.includes(periodId))
          )
        );

        // Clear period rooms for invalid periods
        setPeriodRooms(prev => 
          Object.fromEntries(
            Object.entries(prev).filter(([periodId]) => validPeriodIds.includes(periodId))
          )
        );
      }
    }
  }, [formData.date, availableScheduleTypes, getScheduleTypeForDateCallback, requestMode]);

  // Load tenant configuration (including school holidays)
  // Load tenant configuration (including school holidays)
  // Load tenant configuration (including school holidays)
  const loadTenantConfig = useCallback(async () => {
    if (!currentUser?.tenantId) {
      console.warn('RequestSub: Cannot load config, tenantId is missing.');
      return;
    }

    setIsLoadingConfig(true);
    try {
      let userDivision = currentUser?.division;
      
      if (!userDivision && allFacultyForTenant && allFacultyForTenant.length > 0) {
        const facultyRecord = allFacultyForTenant.find(f => f.email === currentUser?.email);
        userDivision = facultyRecord?.division;
      }
      
      console.log('User division:', userDivision);
      console.log('allFacultyForTenant status:', allFacultyForTenant ? allFacultyForTenant.length + ' records loaded' : 'not loaded yet');
      console.log('DEBUG: Detailed user division detection:');
      console.log('  - currentUser?.division:', currentUser?.division);
      console.log('  - currentUser?.email:', currentUser?.email);
      if (allFacultyForTenant?.length > 0) {
        const facultyRecord = allFacultyForTenant.find(f => f.email === currentUser?.email);
        console.log('  - Faculty record found:', facultyRecord);
        console.log('  - Faculty record division:', facultyRecord?.division);
      }
      console.log('  - FINAL userDivision:', userDivision);

      // Get basic configs
      const config = await TenantConfigService.getAllConfigsForJS(currentUser.tenantId);
      
      // Handle both old array format and new object format for enabled modules
      let isSubstituteManagementEnabled = true;
      
      if (config.enabledModules) {
        if (Array.isArray(config.enabledModules)) {
          isSubstituteManagementEnabled = config.enabledModules.includes('Substitute Management');
        } else if (typeof config.enabledModules === 'object') {
          isSubstituteManagementEnabled = config.enabledModules.substituteManagement !== false;
        }
      }
      
      if (!isSubstituteManagementEnabled) {
        setError('Substitute Management module is not enabled for your organization. Please contact your administrator.');
        setIsLoadingConfig(false);
        return;
      }

      // Manually load schedule types, calendar assignments, school holidays, and divisions
      const [scheduleTypesConfig, calendarAssignmentsConfig, schoolHolidaysConfig, divisionsConfig] = await Promise.all([
        TenantConfigService.getConfig(currentUser.tenantId, 'schedule_types', { scheduleTypes: [], customFields: [] }),
        TenantConfigService.getConfig(currentUser.tenantId, 'calendar_assignments', { patterns: [], specificDates: [] }),
        TenantConfigService.getConfig(currentUser.tenantId, 'school_holidays', { holidays: [] }),
        TenantConfigService.getConfig(currentUser.tenantId, 'divisions', {
          divisions: [
            { code: 'EC', label: 'Early Childhood', color: '#007bff', order: 1 },
            { code: 'LS', label: 'Lower School', color: '#28a745', order: 2 },
            { code: 'MS', label: 'Middle School', color: '#ffc107', order: 3 },
            { code: 'HS', label: 'High School', color: '#dc3545', order: 4 }
          ]
        })
      ]);

      console.log('Raw schedule types config:', scheduleTypesConfig);
      console.log('Raw calendar assignments config:', calendarAssignmentsConfig);
      console.log('Raw school holidays config:', schoolHolidaysConfig);
      console.log('Raw divisions config:', divisionsConfig);

      // Store divisions in state for later use
      setDivisions(divisionsConfig.divisions || []);

      // Ensure scheduleTypes is properly loaded and fallback to periods if needed
      let scheduleTypes = scheduleTypesConfig.scheduleTypes || [];

      // If no scheduleTypes but we have periods (legacy), create a default schedule type
      if (scheduleTypes.length === 0 && config.periods && config.periods.length > 0) {
        scheduleTypes = [{
          id: 'regular',
          name: 'Regular Day',
          description: 'Standard school day',
          periods: config.periods,
          isDefault: true
        }];
      }

      // Resolve calendarAssignmentId from divisions config
      // The division document now has a calendarAssignmentId field that maps to the
      // correct key in calendar_assignments. This is the missing link that was causing
      // everyone to fall back to the default schedule.
      //
      // Flow: User division code (e.g. "B") -> divisions config lookup ->
      //       calendarAssignmentId (e.g. "B") -> calendar_assignments key lookup
      let resolvedCalendarAssignmentKey = userDivision;
      
      if (userDivision && divisionsConfig.divisions) {
        const userDivisionConfig = divisionsConfig.divisions.find(function(d) { return d.code === userDivision; });
        if (userDivisionConfig && userDivisionConfig.calendarAssignmentId) {
          resolvedCalendarAssignmentKey = userDivisionConfig.calendarAssignmentId;
          console.log('Division "' + userDivision + '" maps to calendarAssignmentId: "' + resolvedCalendarAssignmentKey + '"');
        } else {
          console.log('Division "' + userDivision + '" has no calendarAssignmentId field, falling back to division code "' + userDivision + '" as calendar key');
        }
      }

      // Load division-specific calendar assignments
      var calendarAssignments;

      // Check if ANY division-based calendar data exists
      var hasDivisionBasedData = Object.keys(calendarAssignmentsConfig).some(function(key) {
        return typeof calendarAssignmentsConfig[key] === 'object' && 
          calendarAssignmentsConfig[key] !== null &&
          (calendarAssignmentsConfig[key].patterns !== undefined || 
           calendarAssignmentsConfig[key].specificDates !== undefined);
      });

      if (hasDivisionBasedData) {
        console.log('Using division-based calendar structure');
        console.log('Available calendar assignment keys:', Object.keys(calendarAssignmentsConfig).filter(function(key) {
          return typeof calendarAssignmentsConfig[key] === 'object' && calendarAssignmentsConfig[key] !== null;
        }));
        console.log('Resolved calendar assignment key for this user:', resolvedCalendarAssignmentKey);
        
        if (resolvedCalendarAssignmentKey && calendarAssignmentsConfig[resolvedCalendarAssignmentKey]) {
          // Found calendar data using the resolved key (from calendarAssignmentId)
          calendarAssignments = {
            patterns: calendarAssignmentsConfig[resolvedCalendarAssignmentKey].patterns || [],
            specificDates: calendarAssignmentsConfig[resolvedCalendarAssignmentKey].specificDates || []
          };
          console.log('Loaded calendar for key "' + resolvedCalendarAssignmentKey + '" (user division: "' + userDivision + '"):', calendarAssignments);
        } else if (userDivision && calendarAssignmentsConfig[userDivision]) {
          // Fallback: try the raw division code directly (backward compatibility)
          calendarAssignments = {
            patterns: calendarAssignmentsConfig[userDivision].patterns || [],
            specificDates: calendarAssignmentsConfig[userDivision].specificDates || []
          };
          console.log('Loaded calendar using direct division code "' + userDivision + '" (fallback):', calendarAssignments);
        } else if (userDivision) {
          // User has a division but no calendar data found for it
          console.warn('No calendar data found for division: "' + userDivision + '" (tried key: "' + resolvedCalendarAssignmentKey + '" and direct code: "' + userDivision + '")');
          console.warn('Available keys in calendar_assignments:', Object.keys(calendarAssignmentsConfig));
          calendarAssignments = { patterns: [], specificDates: [] };
        } else {
          // User has no division - use combined data from all divisions as fallback
          console.log('User has no division set - combining all division calendars');
          var allPatterns = [];
          var allSpecificDates = [];
          
          // Dynamically get all calendar assignment keys from the loaded divisions config
          // Use calendarAssignmentId when available, fall back to division code
          if (divisionsConfig && divisionsConfig.divisions) {
            var keysToCheck = new Set();
            
            divisionsConfig.divisions.forEach(function(div) {
              var key = div.calendarAssignmentId || div.code;
              if (key) keysToCheck.add(key);
            });
            
            console.log('Combining calendars from keys:', Array.from(keysToCheck));
            
            keysToCheck.forEach(function(key) {
              if (calendarAssignmentsConfig[key]) {
                allPatterns.push.apply(allPatterns, calendarAssignmentsConfig[key].patterns || []);
                allSpecificDates.push.apply(allSpecificDates, calendarAssignmentsConfig[key].specificDates || []);
              }
            });
          }
          
          calendarAssignments = { 
            patterns: allPatterns, 
            specificDates: allSpecificDates 
          };
        }
      } else {
        // Legacy structure - single patterns/specificDates arrays
        console.log('Using legacy calendar structure (no divisions)');
        calendarAssignments = {
          patterns: calendarAssignmentsConfig.patterns || [],
          specificDates: calendarAssignmentsConfig.specificDates || []
        };
      }

      // Load school holidays
      var schoolHolidays = schoolHolidaysConfig.holidays || [];

      var finalConfig = {
        ...config,
        scheduleTypes: scheduleTypes,
        calendarAssignments: calendarAssignments,
        schoolHolidays: schoolHolidays,
        customFields: scheduleTypesConfig.customFields || []
      };

      setTenantConfig(finalConfig);
      console.log('Loaded tenant config successfully:', finalConfig);
      console.log('Schedule types loaded:', scheduleTypes);
      console.log('Calendar assignments loaded:', calendarAssignments);
      console.log('School holidays loaded:', schoolHolidays);
      console.log('Custom fields loaded:', scheduleTypesConfig.customFields);

      // Filter schedule types based on user's division
      // Show if:
      // 1. divisions array is empty/undefined (available to all - for legacy clients)
      // 2. OR divisions array includes the user's division
      var filteredScheduleTypes = scheduleTypes.filter(function(scheduleType) {
        // No divisions specified = available to everyone (backward compatibility)
        if (!scheduleType.divisions || scheduleType.divisions.length === 0) {
          console.log('Schedule "' + scheduleType.name + '" available to ALL (no division restrictions)');
          return true;
        }
        
        // If user has no division, show all schedules (fallback for safety)
        if (!userDivision) {
          console.log('User has no division set - showing schedule "' + scheduleType.name + '"');
          return true;
        }
        
        // Check if user's division is in the schedule's divisions array
        var isAvailable = scheduleType.divisions.includes(userDivision);
        console.log('Schedule "' + scheduleType.name + '" ' + (isAvailable ? 'available' : 'NOT available') + ' to ' + userDivision + ' division');
        return isAvailable;
      });

      setAvailableScheduleTypes(filteredScheduleTypes);
      console.log('User will see ' + filteredScheduleTypes.length + ' out of ' + scheduleTypes.length + ' schedule types');
    } catch (error) {
      console.error('Error loading tenant configuration:', error);
      setError('Failed to load system configuration. Please refresh the page or contact support.');
    } finally {
      setIsLoadingConfig(false);
    }
  }, [currentUser?.tenantId]);

  const fetchFacultyDataForTenant = useCallback(async () => {
    if (!currentUser || !currentUser.tenantId) {
      console.warn('RequestSub: Cannot fetch faculty, currentUser or tenantId is missing.');
      setAllFacultyForTenant([]);
      return;
    }
    setIsLoadingFaculty(true);
    try {
      const facultyQuery = query(
        collection(db, 'faculty_staff'),
        where("tenantId", "==", currentUser.tenantId)
      );
      const snapshot = await getDocs(facultyQuery);
      const facultyList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setAllFacultyForTenant(facultyList);
    } catch (error) {
      console.error('RequestSub: Error fetching faculty data:', error);
      setAllFacultyForTenant([]);
    }
    setIsLoadingFaculty(false);
  }, [currentUser]);

  // Fetch saved substitute groups
  const fetchSavedGroups = useCallback(async () => {
    if (!currentUser || !currentUser.tenantId) return;
    
    setIsLoadingGroups(true);
    try {
      const groupsQuery = query(
        collection(db, 'substitute_groups'),
        where("tenantId", "==", currentUser.tenantId),
        where("createdBy", "==", currentUser.uid)
      );
      const snapshot = await getDocs(groupsQuery);
      const groups = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSavedGroups(groups);
    } catch (error) {
      console.error('Error fetching saved groups:', error);
    }
    setIsLoadingGroups(false);
  }, [currentUser]);

  useEffect(() => {
    if (currentUser && currentUser.tenantId) {
        loadTenantConfig();
        fetchFacultyDataForTenant();
        fetchSavedGroups();
    }
  }, [currentUser, loadTenantConfig, fetchFacultyDataForTenant, fetchSavedGroups]);

  // Re-resolve calendar assignments when admin selects a teacher to submit on behalf of
  // This ensures the schedule shown matches the SELECTED teacher's division, not the admin's
  useEffect(function() {
    if (!isSubmittingForOther || !selectedTeacherNameForAdmin || !formData.department) {
      return;
    }

    // Find the selected teacher in the faculty list
    var selectedTeacher = allFacultyForTenant.find(function(f) {
      return f.name === selectedTeacherNameForAdmin && f.department === formData.department;
    });

    if (!selectedTeacher) {
      console.log('Selected teacher not found in faculty list');
      return;
    }

    var teacherDivision = selectedTeacher.division;
    console.log('Admin submitting for teacher: ' + selectedTeacherNameForAdmin + ', division: ' + teacherDivision);

    if (!teacherDivision) {
      console.log('Selected teacher has no division set, calendar will use combined/default');
      return;
    }

    // We need to re-fetch the calendar assignments and divisions configs to resolve properly
    var resolveTeacherCalendar = async function() {
      try {
        var calendarAssignmentsConfig = await TenantConfigService.getConfig(
          currentUser.tenantId, 'calendar_assignments', { patterns: [], specificDates: [] }
        );
        var divisionsConfig = await TenantConfigService.getConfig(
          currentUser.tenantId, 'divisions', { divisions: [] }
        );

        // Look up the teacher's division to find calendarAssignmentId
        var teacherDivisionConfig = (divisionsConfig.divisions || []).find(function(d) {
          return d.code === teacherDivision;
        });

        var resolvedKey = teacherDivision; // default fallback
        if (teacherDivisionConfig && teacherDivisionConfig.calendarAssignmentId) {
          resolvedKey = teacherDivisionConfig.calendarAssignmentId;
          console.log('Teacher division "' + teacherDivision + '" maps to calendarAssignmentId: "' + resolvedKey + '"');
        }

        var newCalendarAssignments;

        if (resolvedKey && calendarAssignmentsConfig[resolvedKey]) {
          newCalendarAssignments = {
            patterns: calendarAssignmentsConfig[resolvedKey].patterns || [],
            specificDates: calendarAssignmentsConfig[resolvedKey].specificDates || []
          };
          console.log('Loaded calendar for teacher using key "' + resolvedKey + '":', newCalendarAssignments);
        } else if (calendarAssignmentsConfig[teacherDivision]) {
          newCalendarAssignments = {
            patterns: calendarAssignmentsConfig[teacherDivision].patterns || [],
            specificDates: calendarAssignmentsConfig[teacherDivision].specificDates || []
          };
          console.log('Loaded calendar for teacher using direct division code "' + teacherDivision + '"');
        } else {
          console.warn('No calendar data found for teacher division: "' + teacherDivision + '"');
          return; // Don't update if we can't find anything
        }

        // Update tenantConfig with the teacher's calendar assignments
        setTenantConfig(function(prev) {
          return {
            ...prev,
            calendarAssignments: newCalendarAssignments
          };
        });

        // Also filter schedule types for this teacher's division
        var allScheduleTypes = tenantConfig.scheduleTypes || [];
        var filtered = allScheduleTypes.filter(function(scheduleType) {
          if (!scheduleType.divisions || scheduleType.divisions.length === 0) {
            return true;
          }
          return scheduleType.divisions.includes(teacherDivision);
        });

        setAvailableScheduleTypes(filtered);
        console.log('Updated to teacher division "' + teacherDivision + '": ' + filtered.length + ' schedule types available');

      } catch (err) {
        console.error('Error resolving teacher calendar:', err);
      }
    };

    resolveTeacherCalendar();
  }, [isSubmittingForOther, selectedTeacherNameForAdmin, formData.department, allFacultyForTenant, currentUser?.tenantId]);

  useEffect(() => {
    if (currentUser) {
      if (canSubmitForOthers && isSubmittingForOther) {
        setFormData(prev => ({ ...initialFormState, department: prev.department }));
        setSelectedTeacherNameForAdmin('');
      } else {
        const currentFacultyMember = allFacultyForTenant.find(f => f.email === currentUser.email);
        setFormData(prev => ({
          ...initialFormState,
          name: currentFacultyMember ? currentFacultyMember.displayName || currentFacultyMember.name || currentUser.displayName || currentUser.email : currentUser.displayName || currentUser.email,
          department: currentFacultyMember ? currentFacultyMember.department : (prev.department || '')
        }));

        if (canSubmitForOthers) {
          setIsSubmittingForOther(false);
        }
        setSelectedTeacherNameForAdmin('');
      }
    }
  }, [currentUser, allFacultyForTenant, canSubmitForOthers, isSubmittingForOther]);

  // Use dynamic departments from configuration
  const departments = React.useMemo(() => {
    const allDepts = [
      ...tenantConfig.academicDepartments,
    ];
    
      const facultyDepts = allFacultyForTenant
  .map(f => f.department)
  .filter(dep => {
    return dep && 
           dep.trim() !== "" && 
           tenantConfig.academicDepartments.includes(dep);
  });
    
    const combined = [...new Set([...allDepts, ...facultyDepts])];
    return combined.filter(dept => dept && dept.trim() !== "").sort();
  }, [tenantConfig.academicDepartments, allFacultyForTenant]);
  
  const teachersForAdminSelection = allFacultyForTenant
    .filter(f => formData.department && f.department === formData.department)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const availableSubstitutes = allFacultyForTenant
    .filter(f => f.email !== currentUser.email)
    .sort((a, b) => {
    // First sort by role - Outside Subs go to bottom
    const aIsOutsideSub = a.role === 'Outside Sub';
    const bIsOutsideSub = b.role === 'Outside Sub';
    
    if (aIsOutsideSub && !bIsOutsideSub) return 1;  // a goes after b
    if (!aIsOutsideSub && bIsOutsideSub) return -1; // a goes before b
    
    // If both have same role status, sort alphabetically by name
    return (a.name || '').localeCompare(b.name || '');
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    if (canSubmitForOthers && isSubmittingForOther && name === "name") {
        setSelectedTeacherNameForAdmin(value); 
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  
  const handleDepartmentChangeForAdmin = (e) => {
    setFormData(prev => ({ ...prev, department: e.target.value, name: '' }));
    setSelectedTeacherNameForAdmin('');
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const updatedPeriods = checked
        ? [...prev.periods, value]
        : prev.periods.filter(p => p !== value);
      
      const updatedNotes = { ...prev.periodNotes };
      const updatedPeriodRooms = { ...prev.periodRooms };
      if (!checked) {
        delete updatedNotes[value];
        delete updatedPeriodRooms[value];
      }

      setPeriodFiles(prevFiles => {
        const updatedFiles = { ...prevFiles };
        if (!checked) delete updatedFiles[value];
        return updatedFiles;
      });

      setAdditionalPeriodFiles(prevFiles => {
        const updatedFiles = { ...prevFiles };
        if (!checked) delete updatedFiles[value];
        return updatedFiles;
      });

      // Update periodRooms state as well
      setPeriodRooms(updatedPeriodRooms);
      
      return { 
        ...prev, 
        periods: updatedPeriods, 
        periodNotes: updatedNotes,
        periodRooms: updatedPeriodRooms
      };
    });
  };

  const handleNoteChange = (period, note) => {
    setFormData(prev => ({ ...prev, periodNotes: { ...prev.periodNotes, [period]: note } }));
  };

  // Function to handle period room changes
  const handlePeriodRoomChange = (period, room) => {
    setPeriodRooms(prev => ({ ...prev, [period]: room }));
    // Also update formData to include periodRooms
    setFormData(prev => ({ 
      ...prev, 
      periodRooms: { ...prev.periodRooms, [period]: room }
    }));
  };

  const handleFileChange = (period, file) => {
    setPeriodFiles(prev => ({ ...prev, [period]: file }));
  };

  const handleAdditionalFilesChange = (period, files) => {
    const fileArray = Array.from(files);
    setAdditionalPeriodFiles(prev => ({
      ...prev,
      [period]: [...(prev[period] || []), ...fileArray]
    }));
  };

  const removeAdditionalFile = (period, indexToRemove) => {
    setAdditionalPeriodFiles(prev => ({
      ...prev,
      [period]: prev[period]?.filter((_, index) => index !== indexToRemove) || []
    }));
  };

  // Handle sharing option changes
  const handleSharingChange = (field, value) => {
    setSharingOptions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDepartmentSharingChange = (department, checked) => {
    setSharingOptions(prev => ({
      ...prev,
      shareWithDepartments: checked 
        ? [...prev.shareWithDepartments, department]
        : prev.shareWithDepartments.filter(d => d !== department)
    }));
  };

  const handlePreferredSubChange = (sub, checked) => {
    setSharingOptions(prev => ({
      ...prev,
      selectedPreferredSubs: checked
        ? [...prev.selectedPreferredSubs, sub]
        : prev.selectedPreferredSubs.filter(s => s.id !== sub.id)
    }));
  };

  const handleGroupSelection = (groupId) => {
    setSelectedGroup(groupId);
    if (groupId) {
      const group = savedGroups.find(g => g.id === groupId);
      if (group) {
        setSharingOptions(prev => ({
          ...prev,
          selectedPreferredSubs: group.members || []
        }));
      }
    } else {
      setSharingOptions(prev => ({
        ...prev,
        selectedPreferredSubs: []
      }));
    }
  };

  const removePreferredSub = (subToRemove) => {
    setSharingOptions(prev => ({
      ...prev,
      selectedPreferredSubs: prev.selectedPreferredSubs.filter(s => s.id !== subToRemove.id)
    }));
  };

  // Save group function
  const saveGroup = async () => {
    if (!sharingOptions.newGroupName.trim() || sharingOptions.selectedPreferredSubs.length === 0) {
      setError('Please enter a group name and select at least one substitute to save as a group.');
      return;
    }

    try {
      const groupData = {
        name: sharingOptions.newGroupName.trim(),
        createdBy: currentUser.uid,
        tenantId: currentUser.tenantId,
        isSchoolWide: false,
        members: sharingOptions.selectedPreferredSubs,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      await addDoc(collection(db, 'substitute_groups'), groupData);
      
      await fetchSavedGroups();
      
      setSharingOptions(prev => ({
        ...prev,
        newGroupName: '',
        saveAsGroup: false
      }));
      
      setSuccessMessage('Group saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving group:', error);
      setError('Failed to save group. Please try again.');
    }
  };

  // JSX for the enhanced date selection section
  const renderDateSelection = () => {
    return (
      <div style={{ marginBottom: '15px' }}>
        <label><strong>Request Type *</strong></label><br />
        <div style={{ display: 'flex', gap: '20px', marginTop: '8px', marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="requestMode"
              value="single"
              checked={requestMode === 'single'}
              onChange={(e) => {
                setRequestMode(e.target.value);
                setSelectedDates([]);
                setDateSpecificSettings({});
                setStartDate('');
                setEndDate('');
              }}
            />
            Single Day Request
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="radio"
              name="requestMode"
              value="multiple"
              checked={requestMode === 'multiple'}
              onChange={(e) => {
                setRequestMode(e.target.value);
                setFormData(prev => ({ ...prev, date: '' }));
              }}
            />
            Multiple Days Request
          </label>
        </div>

        {requestMode === 'single' ? (
          <div>
            <label><strong>Date of Absence *</strong></label><br />
            <input 
              type="date" 
              name="date" 
              value={formData.date} 
              onChange={handleChange} 
              required 
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }} 
            />
            
            {/* Show schedule info for selected date */}
            {formData.date && selectedDateScheduleInfo && (
              <div style={{ 
                marginTop: '8px', 
                padding: '10px', 
                backgroundColor: selectedDateScheduleInfo.source === 'holiday' ? '#fff3cd' : 
                                 selectedDateScheduleInfo.source === 'weekend' ? '#f8f9fa' : '#e7f3ff', 
                border: `1px solid ${selectedDateScheduleInfo.source === 'holiday' ? '#ffeaa7' : 
                                     selectedDateScheduleInfo.source === 'weekend' ? '#dee2e6' : '#b8daff'}`, 
                borderRadius: '4px' 
              }}>
                <strong>Schedule for {formData.date}:</strong>
                <div style={{ marginTop: '4px', fontSize: '14px' }}>
                  {selectedDateScheduleInfo.source === 'holiday' ? (
                    <span style={{ fontWeight: 'bold', color: '#856404' }}>
                      School Holiday: {selectedDateScheduleInfo.holidayInfo?.holidayName}
                    </span>
                  ) : selectedDateScheduleInfo.source === 'weekend' ? (
                    <span style={{ fontWeight: 'bold', color: '#6c757d' }}>
                      Weekend - No School
                    </span>
                  ) : (
                    <span style={{ fontWeight: 'bold', color: '#007bff' }}>
                      {selectedDateScheduleInfo.scheduleType?.name || 'Unknown Schedule'}
                    </span>
                  )}
                  {selectedDateScheduleInfo.description && (
                    <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                      ({selectedDateScheduleInfo.description})
                    </span>
                  )}
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#6c757d' }}>
                    {selectedDateScheduleInfo.source === 'specific' && 'Special date override'}
                    {selectedDateScheduleInfo.source === 'weekly' && `Weekly pattern: ${selectedDateScheduleInfo.pattern}`}
                    {selectedDateScheduleInfo.source === 'rotating' && `Rotating pattern: ${selectedDateScheduleInfo.pattern} (Day ${selectedDateScheduleInfo.rotationDay})`}
                    {selectedDateScheduleInfo.source === 'default' && 'Default schedule'}
                    {selectedDateScheduleInfo.source === 'holiday' && 
                      selectedDateScheduleInfo.holidayInfo?.holidayType === 'range' && 
                      ` (${selectedDateScheduleInfo.holidayInfo.startDate} to ${selectedDateScheduleInfo.holidayInfo.endDate})`}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            <label><strong>Multiple Dates Selection *</strong></label><br />
            
            {/* Date Range Selection */}
            <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <h5 style={{ margin: '0 0 10px 0' }}>Option 1: Select Date Range</h5>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold' }}>From:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ margin: '5px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '14px', fontWeight: 'bold' }}>To:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    style={{ margin: '5px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDateRangeChange}
                  disabled={!startDate || !endDate}
                  style={{
                    backgroundColor: (!startDate || !endDate) ? '#ccc' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    cursor: (!startDate || !endDate) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Add Range
                </button>
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '5px' }}>
                * Weekends and school holidays will be automatically excluded
              </div>
            </div>

            {/* Individual Date Selection */}
            <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #dee2e6', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
              <h5 style={{ margin: '0 0 10px 0' }}>Option 2: Select Individual Dates</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
                {/* Generate next 30 days for selection */}
                {Array.from({ length: 30 }, (_, i) => {
                  const date = new Date();
                  date.setDate(date.getDate() + i + 1);
                  const dateString = date.toISOString().split('T')[0];
                  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const holidayCheck = isSchoolHoliday(dateString, tenantConfig.schoolHolidays);
                  const isUnavailable = isWeekend || holidayCheck.isHoliday;
                  
                  return (
                    <label
                      key={dateString}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: selectedDates.includes(dateString) ? '#e8f5e8' : 
                                       holidayCheck.isHoliday ? '#fff3cd' :
                                       isWeekend ? '#f5f5f5' : 'white',
                        cursor: isUnavailable ? 'not-allowed' : 'pointer',
                        opacity: isUnavailable ? 0.6 : 1
                      }}
                      title={holidayCheck.isHoliday ? `Holiday: ${holidayCheck.holidayName}` : 
                             isWeekend ? 'Weekend' : 'Available school day'}
                    >
                      <input
                        type="checkbox"
                        checked={selectedDates.includes(dateString)}
                        onChange={(e) => handleIndividualDateChange(dateString, e.target.checked)}
                        disabled={isUnavailable}
                      />
                      <span style={{ fontSize: '14px' }}>
                        {dayName} {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        {holidayCheck.isHoliday && <span style={{ color: '#856404', marginLeft: '4px' }}>🏖️</span>}
                        {isWeekend && <span style={{ color: '#6c757d', marginLeft: '4px' }}>📅</span>}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Selected Dates Display */}
            {selectedDates.length > 0 && (
              <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '8px' }}>
                <h5 style={{ margin: '0 0 10px 0' }}>Selected Dates ({selectedDates.length}):</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
                  {selectedDates.map(date => {
                    const dateObj = new Date(date);
                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
                    return (
                      <span
                        key={date}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '4px 8px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}
                      >
                        {dayName} {dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        <button
                          type="button"
                          onClick={() => handleIndividualDateChange(date, false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'white',
                            cursor: 'pointer',
                            padding: '0',
                            marginLeft: '4px',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
                
                {/* Copy Settings Option */}
                <div style={{ marginTop: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <input
                      type="checkbox"
                      checked={copySettingsToAll}
                      onChange={(e) => setCopySettingsToAll(e.target.checked)}
                    />
                    <span style={{ fontWeight: 'bold' }}>
                      Use same settings (periods, notes, reason) for all selected dates
                    </span>
                  </label>
                  
                  {!copySettingsToAll && (
                    <button
                      type="button"
                      onClick={copyCurrentSettingsToAllDates}
                      style={{
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Copy Current Settings to All Dates
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // JSX for rendering periods section for multiple dates
  const renderMultipleDatePeriods = () => {
    if (requestMode !== 'multiple' || selectedDates.length === 0) return null;

    if (copySettingsToAll) {
      // Show unified period selection that applies to all dates
      const allAvailablePeriods = new Set();
      selectedDates.forEach(date => {
        const settings = dateSpecificSettings[date];
        if (settings && settings.availablePeriods) {
          settings.availablePeriods.forEach(period => allAvailablePeriods.add(JSON.stringify(period)));
        }
      });
      
      const commonPeriods = Array.from(allAvailablePeriods).map(p => JSON.parse(p));
      
      return (
        <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            Period Selection (Applied to All Dates)
          </h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label><strong>Periods Missed *</strong></label><br />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
              {commonPeriods.map(period => (
                <label key={period.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '5px', 
                  padding: '8px 12px', 
                  border: '1px solid #dee2e6', 
                  borderRadius: '4px', 
                  backgroundColor: formData.periods.includes(period.id) ? '#e3f2fd' : 'white',
                  cursor: 'pointer'
                }}>
                  <input 
                    type="checkbox" 
                    value={period.id} 
                    checked={formData.periods.includes(period.id)} 
                    onChange={handleCheckboxChange}
                  />
                  {period.name}
                  {period.start_time && period.end_time && (
                    <small style={{ color: '#6c757d', marginLeft: '4px' }}>
                      ({period.start_time} - {period.end_time})
                    </small>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Common reason and room */}
          <div style={{ marginBottom: '15px' }}>
            <label><strong>Reason for Absence *</strong></label><br />
            <input 
              type="text" 
              name="reason" 
              value={formData.reason} 
              onChange={handleChange} 
              required 
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }} 
              placeholder="This reason will apply to all selected dates"
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label><strong>Room Number</strong></label><br />
            
            {/* Multiple Rooms Checkbox */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={useMultipleRooms}
                  onChange={(e) => {
                    setUseMultipleRooms(e.target.checked);
                    if (!e.target.checked) {
                      // Clear period rooms when switching back to single room
                      setPeriodRooms({});
                      setFormData(prev => ({ ...prev, periodRooms: {} }));
                    } else {
                      // Clear main room when switching to multiple rooms
                      setFormData(prev => ({ ...prev, room: '' }));
                    }
                  }}
                  style={{ marginRight: '8px' }}
                />
                <span>Different room for each period</span>
              </label>
            </div>

            {/* Single Room Input (when not using multiple rooms) */}
            {!useMultipleRooms && (
              <input 
                type="text" 
                name="room" 
                value={formData.room} 
                onChange={handleChange} 
                placeholder="e.g., 203B or Lab A (applies to all dates)" 
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            )}

            {/* Multiple Rooms Notice */}
            {useMultipleRooms && (
              <div style={{ 
                padding: '10px', 
                backgroundColor: '#e7f3ff', 
                border: '1px solid #b8daff', 
                borderRadius: '4px',
                fontSize: '14px',
                color: '#0c5460'
              }}>
                Room numbers will be specified for each selected period below.
              </div>
            )}
          </div>
        </div>
      );
    } else {
      // Show individual settings for each date
      return (
        <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            Individual Date Settings
          </h3>
          
          {selectedDates.map(date => {
            const settings = dateSpecificSettings[date] || {};
            const dateObj = new Date(date);
            const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
            
            return (
              <div key={date} style={{ 
                border: '1px solid #eee', 
                borderRadius: '8px', 
                padding: '15px', 
                marginBottom: '15px', 
                backgroundColor: '#f8f9fa' 
              }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                  {dayName}, {dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  {settings.scheduleInfo && (
                    <small style={{ color: '#6c757d', marginLeft: '8px' }}>
                      ({settings.scheduleInfo.scheduleType?.name})
                    </small>
                  )}
                </h4>
                
                {/* Periods for this date */}
                <div style={{ marginBottom: '15px' }}>
                  <label><strong>Periods for {date}:</strong></label><br />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                    {settings.availablePeriods?.map(period => (
                      <label key={`${date}-${period.id}`} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '5px', 
                        padding: '6px 10px', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '4px', 
                        backgroundColor: settings.periods?.includes(period.id) ? '#e3f2fd' : 'white',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}>
                        <input 
                          type="checkbox" 
                          value={period.id} 
                          checked={settings.periods?.includes(period.id) || false}
                          onChange={(e) => {
                            const updatedPeriods = e.target.checked 
                              ? [...(settings.periods || []), period.id]
                              : (settings.periods || []).filter(p => p !== period.id);
                            updateDateSpecificSetting(date, 'periods', updatedPeriods);
                          }}
                        />
                        {period.name}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Reason for this date */}
                <div style={{ marginBottom: '15px' }}>
                  <label><strong>Reason for {date}:</strong></label><br />
                  <input 
                    type="text" 
                    value={settings.reason || ''} 
                    onChange={(e) => updateDateSpecificSetting(date, 'reason', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                    placeholder="Reason for this specific date"
                  />
                </div>

                {/* Room for this date */}
                <div style={{ marginBottom: '15px' }}>
                  <label><strong>Room for {date}:</strong></label><br />
                  <input 
                    type="text" 
                    value={settings.room || ''} 
                    onChange={(e) => updateDateSpecificSetting(date, 'room', e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} 
                    placeholder="Room number for this date"
                  />
                </div>
              </div>
            );
          })}
        </div>
      );
    }
  };

  // Modified handleSubmit function for multiple dates (with database writes!)
  // Add this state at the top of your component with other useState declarations
const [lastSubmitTime, setLastSubmitTime] = useState(0);

const handleSubmit = async (e) => {
  e.preventDefault();
  
  // ✅ LAYER 1: Prevent rapid double-clicks with state check
  if (isSubmitting) {
    console.log('⚠️ Already submitting, ignoring duplicate click');
    return;
  }

  // ✅ LAYER 2: Time-based debouncing (prevent submissions within 3 seconds)
  const now = Date.now();
  if (now - lastSubmitTime < 3000) {
    console.log('⚠️ Submission too soon after last one, ignoring');
    setError('Please wait a moment before submitting again.');
    setTimeout(() => setError(''), 2000);
    return;
  }
  setLastSubmitTime(now);

  setIsSubmitting(true);
  setError('');
  setSuccessMessage('');

  try {
    const requests = [];
    
    // Get Firebase Auth user for token information
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('User not authenticated with Firebase Auth');
    }

    // Get token with claims
    const token = await firebaseUser.getIdTokenResult();

    // Validation and User Identification Logic
    let requesterDetails = {};
    if (canSubmitForOthers && isSubmittingForOther) {
      const teacherBeingSubmittedFor = allFacultyForTenant.find(f => f.name === selectedTeacherNameForAdmin && f.department === formData.department);
      if (!teacherBeingSubmittedFor) {
        setError("Could not find the selected teacher's details. Please re-select.");
        setIsSubmitting(false);
        return;
      }
      requesterDetails = { name: teacherBeingSubmittedFor.name, email: teacherBeingSubmittedFor.email, uid: teacherBeingSubmittedFor.uid || firebaseUser.uid };
    } else {
      requesterDetails = { name: currentUser.displayName, email: currentUser.email, uid: firebaseUser.uid };
    }

    if (!formData.department) {
      setError("Please select a department.");
      setIsSubmitting(false);
      return;
    }
    
    if (requestMode === 'single') {
      // Handle single date
      if (!formData.date || !formData.reason || formData.periods.length === 0) {
        setError("Please complete all required fields: Date, Reason, and at least one Period.");
        setIsSubmitting(false);
        return;
      }

      // Check if selected date is a holiday or weekend
      const holidayCheck = isSchoolHoliday(formData.date, tenantConfig.schoolHolidays);
      if (holidayCheck.isHoliday) {
        setError(`Cannot request substitute for ${formData.date} - this is a school holiday: ${holidayCheck.holidayName}`);
        setIsSubmitting(false);
        return;
      }

      const dateObj = new Date(formData.date + 'T12:00:00');
      const dayOfWeek = dateObj.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        setError(`Cannot request substitute for ${formData.date} - weekends are not school days.`);
        setIsSubmitting(false);
        return;
      }

      // Validate periods against the selected date's schedule
      if (availablePeriods.length === 0) {
        setError("No periods are available for the selected date. Please choose a different date or contact your administrator.");
        setIsSubmitting(false);
        return;
      }

      const validPeriodIds = availablePeriods.map(p => p.id);
      const invalidPeriods = formData.periods.filter(p => !validPeriodIds.includes(p));
      if (invalidPeriods.length > 0) {
        setError(`Invalid periods selected: ${invalidPeriods.join(', ')}. Please refresh and try again.`);
        setIsSubmitting(false);
        return;
      }

      // ✅ LAYER 3: Check for duplicate requests in database
      console.log('🔍 Checking for duplicate requests...');
      try {
        const duplicateCheckQuery = query(
          collection(db, 'sub_requests'),
          where("tenantId", "==", token.claims.tenantId || currentUser.tenantId),
          where("requesterEmail", "==", requesterDetails.email.toLowerCase()),
          where("date", "==", formData.date),
          where("name", "==", requesterDetails.name)
        );
        
        const existingSnapshot = await getDocs(duplicateCheckQuery);
        
        // ✅ IMPROVED: Block if ANY periods are duplicated
          if (!existingSnapshot.empty) {
            // Check all existing requests for this date (might be multiple if periods were split)
            let hasDuplicatePeriods = false;
            let duplicatePeriods = [];
            
            existingSnapshot.docs.forEach(existingDoc => {
              const existingRequest = existingDoc.data();
              const existingPeriods = existingRequest.periods || [];
              const newPeriods = formData.periods || [];
              
              // Find common periods
              const commonPeriods = existingPeriods.filter(p => newPeriods.includes(p));
              
              if (commonPeriods.length > 0) {
                hasDuplicatePeriods = true;
                duplicatePeriods = [...new Set([...duplicatePeriods, ...commonPeriods])];
              }
            });
            
            if (hasDuplicatePeriods) {
              console.log('⚠️ Duplicate periods detected!', {
                duplicatePeriods,
                date: formData.date
              });
              
              setError(
                `You already have a substitute request for ${formData.date} that includes ` +
                `${duplicatePeriods.length > 1 ? 'periods' : 'period'} ${duplicatePeriods.join(', ')}. ` +
                `Please check "My Relief Roster" to view or edit your existing request.`
              );
              setIsSubmitting(false);
              return;
            }
          }
        
        console.log('✅ No duplicate found, proceeding with submission');
      } catch (duplicateCheckError) {
        console.error('Error checking for duplicates:', duplicateCheckError);
        // Continue anyway - better to allow submission than block due to check error
      }

      // Extract custom field values from formData
      const customFieldValues = {};
      if (tenantConfig.customFields) {
        tenantConfig.customFields.forEach(field => {
          const fieldName = `customField_${field.id}`;
          if (formData[fieldName] !== undefined) {
            customFieldValues[field.id] = formData[fieldName];
          }
        });
      }
      const userFacultyRecord = allFacultyForTenant.find(f => f.email === currentUser.email) || {};
      
      // Create single request with undefined value filtering
      const rawRequestData = {
        department: formData.department,
        division: currentUser.division || userFacultyRecord.division || '',
        name: requesterDetails.name,
        date: formData.date,
        reason: formData.reason,
        periods: formData.periods.sort((a,b) => Number(a) - Number(b)),
        room: useMultipleRooms ? '' : formData.room,
        periodRooms: useMultipleRooms ? periodRooms : {},
        periodNotes: formData.periodNotes,
        customFields: customFieldValues,
        timestamp: Timestamp.now(),
        requesterEmail: requesterDetails.email,
        requesterUid: requesterDetails.uid,
        tenantId: token.claims.tenantId || currentUser.tenantId,
        filled: {},
        submittedByPrivilegedUserUid: (canSubmitForOthers && isSubmittingForOther) ? currentUser.uid : undefined,
        submittedByPrivilegedUserName: (canSubmitForOthers && isSubmittingForOther) ? currentUser.displayName : undefined,
        scheduleInfo: selectedDateScheduleInfo,
        useMultipleRooms: useMultipleRooms
      };

      // Clean the request data to remove undefined values
      const requestData = Object.fromEntries(
        Object.entries(rawRequestData).filter(([_, value]) => value !== undefined)
      );
      requests.push(requestData);
      
    } else {
      // Handle multiple dates
      if (selectedDates.length === 0) {
        setError("Please select at least one date.");
        setIsSubmitting(false);
        return;
      }

      // Validate all dates are not holidays or weekends
      for (const date of selectedDates) {
        const holidayCheck = isSchoolHoliday(date, tenantConfig.schoolHolidays);
        if (holidayCheck.isHoliday) {
          setError(`Cannot request substitute for ${date} - this is a school holiday: ${holidayCheck.holidayName}. Please remove this date from your selection.`);
          setIsSubmitting(false);
          return;
        }

        const dateObj = new Date(date + 'T12:00:00');
        const dayOfWeek = dateObj.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          setError(`Cannot request substitute for ${date} - weekends are not school days. Please remove this date from your selection.`);
          setIsSubmitting(false);
          return;
        }
      }

      // ✅ LAYER 3: Check for duplicates in multiple dates
      console.log('🔍 Checking for duplicate requests across multiple dates...');
      try {
        for (const date of selectedDates) {
          const duplicateCheckQuery = query(
            collection(db, 'sub_requests'),
            where("tenantId", "==", token.claims.tenantId || currentUser.tenantId),
            where("requesterEmail", "==", requesterDetails.email.toLowerCase()),
            where("date", "==", date),
            where("name", "==", requesterDetails.name)
          );
          
          const existingSnapshot = await getDocs(duplicateCheckQuery);
          
          if (!existingSnapshot.empty) {
          // Check for period overlap
          let hasDuplicatePeriods = false;
          let duplicatePeriods = [];
          const settings = copySettingsToAll ? formData : dateSpecificSettings[date];
          const newPeriods = settings.periods || [];
          
          existingSnapshot.docs.forEach(existingDoc => {
            const existingRequest = existingDoc.data();
            const existingPeriods = existingRequest.periods || [];
            const commonPeriods = existingPeriods.filter(p => newPeriods.includes(p));
            
            if (commonPeriods.length > 0) {
              hasDuplicatePeriods = true;
              duplicatePeriods = [...new Set([...duplicatePeriods, ...commonPeriods])];
            }
          });
          
          if (hasDuplicatePeriods) {
            setError(
              `You already have a substitute request for ${date} that includes ` +
              `${duplicatePeriods.length > 1 ? 'periods' : 'period'} ${duplicatePeriods.join(', ')}. ` +
              `Please remove this date or check your existing requests.`
            );
            setIsSubmitting(false);
            return;
          }
        }
      }
        console.log('✅ No duplicates found for any dates, proceeding with submission');
      } catch (duplicateCheckError) {
        console.error('Error checking for duplicates:', duplicateCheckError);
        // Continue anyway
      }

      for (const date of selectedDates) {
        const userFacultyRecord = allFacultyForTenant.find(f => f.email === currentUser.email) || {};
        
        const settings = copySettingsToAll ? 
                        {
                          periods: formData.periods,
                          reason: formData.reason,
                          room: formData.room,
                          periodNotes: formData.periodNotes,
                          periodRooms: formData.periodRooms
                        } : 
                        dateSpecificSettings[date];

        if (!settings.reason || !settings.periods || settings.periods.length === 0) {
          setError(`Please complete all required fields for ${date}: Reason and at least one Period.`);
          setIsSubmitting(false);
          return;
        }

        // Extract custom field values for multi-day requests
        const customFieldValues = {};
        if (tenantConfig.customFields && copySettingsToAll) {
          tenantConfig.customFields.forEach(field => {
            const fieldName = `customField_${field.id}`;
            if (formData[fieldName] !== undefined) {
              customFieldValues[field.id] = formData[fieldName];
            }
          });
        }

        const rawMultipleRequestData = {
          department: formData.department,
          division: currentUser.division || userFacultyRecord.division || '',
          name: requesterDetails.name,
          date: date,
          reason: settings.reason,
          periods: settings.periods.sort((a,b) => Number(a) - Number(b)),
          room: useMultipleRooms ? '' : (settings.room || ''),
          periodRooms: useMultipleRooms ? (settings.periodRooms || {}) : {},
          periodNotes: settings.periodNotes || {},
          customFields: customFieldValues,
          timestamp: Timestamp.now(),
          requesterEmail: requesterDetails.email,
          requesterUid: requesterDetails.uid,
          tenantId: token.claims.tenantId || currentUser.tenantId,
          filled: {},
          submittedByPrivilegedUserUid: (canSubmitForOthers && isSubmittingForOther) ? currentUser.uid : undefined,
          submittedByPrivilegedUserName: (canSubmitForOthers && isSubmittingForOther) ? currentUser.displayName : undefined,
          scheduleInfo: dateSpecificSettings[date]?.scheduleInfo,
          useMultipleRooms: useMultipleRooms
        };

        // Clean the multiple request data too
        const cleanedMultipleRequestData = Object.fromEntries(
          Object.entries(rawMultipleRequestData).filter(([_, value]) => value !== undefined)
        );
        requests.push(cleanedMultipleRequestData);
      }
    }

    // Submit all requests to Firestore
    console.log(`📝 Submitting ${requests.length} request(s) to Firestore...`);
    const requestIds = [];
    
    for (const requestData of requests) {
      console.log('Submitting request:', requestData);
      console.log('🔍 DEBUG - Auth token email:', auth.currentUser?.email);
      console.log('🔍 DEBUG - Request data requesterEmail:', requestData.requesterEmail);
      console.log('🔍 DEBUG - Request data requesterUid:', requestData.requesterUid);
      console.log('🔍 DEBUG - Auth current user UID:', auth.currentUser?.uid);
      console.log('🔍 DEBUG - Are emails equal?:', auth.currentUser?.email?.toLowerCase() === requestData.requesterEmail?.toLowerCase());
      console.log('🔍 DEBUG - Are UIDs equal?:', auth.currentUser?.uid === requestData.requesterUid);
      
      const docRef = await addDoc(collection(db, 'sub_requests'), requestData);
      requestIds.push({ id: docRef.id, date: requestData.date });
      
      // Add shareWith data to the request document if sharing options exist
      const hasSharing = sharingOptions.shareWithDepartments.length > 0 || 
                        sharingOptions.selectedPreferredSubs.length > 0 || 
                        sharingOptions.shareWithAllFaculty;
      
      if (hasSharing) {
        await updateDoc(doc(db, 'sub_requests', docRef.id), {
          shareWith: {
            departments: sharingOptions.shareWithDepartments,
            preferredSubs: sharingOptions.selectedPreferredSubs,
            allFaculty: sharingOptions.shareWithAllFaculty,
            notificationSent: false,
            notificationTimestamp: null,
            recipientCount: 0
          }
        });
      }
    }

    console.log('✅ Successfully submitted requests with IDs:', requestIds);

    // Handle file uploads for single day requests
    if (requestMode === 'single' && requestIds.length > 0) {
      const docRef = { id: requestIds[0].id };
      
      // Upload primary files
      for (const period of formData.periods) {
        if (periodFiles[period]) {
          const file = periodFiles[period];
          const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
          const filePath = `sub_files/${currentUser.tenantId}/${docRef.id}/period_${period}_${safeFileName}`; 
          
          const storageRef = ref(storage, filePath);
          await uploadBytes(storageRef, file);
        }
      }

      // Upload additional files for each period
      for (const period of formData.periods) {
        const additionalFiles = additionalPeriodFiles[period];
        if (additionalFiles && additionalFiles.length > 0) {
          for (let i = 0; i < additionalFiles.length; i++) {
            const file = additionalFiles[i];
            const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const timestamp = Date.now();
            const filePath = `sub_files/${currentUser.tenantId}/${docRef.id}/period_${period}_additional_${timestamp}_${i}_${safeFileName}`;
            
            const storageRef = ref(storage, filePath);
            await uploadBytes(storageRef, file);
          }
        }
      }
    }

    // Send notifications if sharing options are selected
    const hasSharing = sharingOptions.shareWithDepartments.length > 0 || 
                      sharingOptions.selectedPreferredSubs.length > 0 || 
                      sharingOptions.shareWithAllFaculty;
    
    // Save as group if requested
    if (sharingOptions.saveAsGroup && sharingOptions.newGroupName.trim() && sharingOptions.selectedPreferredSubs.length > 0) {
      await saveGroup();
    }
    
    let successMsg = `Successfully submitted ${requests.length} substitute request${requests.length > 1 ? 's' : ''}!`;
    if (hasSharing) {
      const recipientCount = new Set([
        ...sharingOptions.shareWithDepartments.flatMap(dept => 
          allFacultyForTenant.filter(f => (f.department === dept || f.secondaryDepartment === dept) && f.email !== currentUser.email)
        ),
        ...sharingOptions.selectedPreferredSubs,
        ...(sharingOptions.shareWithAllFaculty ? allFacultyForTenant.filter(f => f.email !== currentUser.email) : [])
      ]).size;
      successMsg += ` Notifications sent to ${recipientCount} potential substitute${recipientCount !== 1 ? 's' : ''}.`;
    }
    
    setSuccessMessage(successMsg);
    
    // Reset form (including custom fields)
    const resetCustomFields = {};
    if (tenantConfig.customFields) {
      tenantConfig.customFields.forEach(field => {
        resetCustomFields[`customField_${field.id}`] = field.type === 'checkbox' ? false : '';
      });
    }
    
    setFormData({
      ...initialFormState,
      ...resetCustomFields
    });
    setPeriodFiles({});
    setAdditionalPeriodFiles({});
    setUseMultipleRooms(false);
    setPeriodRooms({});
    setRequestMode('single');
    setSelectedDates([]);
    setDateSpecificSettings({});
    setStartDate('');
    setEndDate('');
    setCopySettingsToAll(true);
    setSharingOptions({
      shareWithDepartments: [],
      shareWithPreferredSubs: false,
      shareWithAllFaculty: false,
      selectedPreferredSubs: [],
      newGroupName: '',
      saveAsGroup: false
    });
    setSelectedGroup('');
    
    if (canSubmitForOthers) { 
      setIsSubmittingForOther(false); 
      setSelectedTeacherNameForAdmin('');
    }
    setTimeout(() => setSuccessMessage(''), 8000);

  } catch (err) {
    console.error('❌ Error submitting sub request:', err);
    setError(`Error: ${err.message || 'An unexpected error occurred.'}`);
  } finally {
    // ✅ CRITICAL: Always reset isSubmitting in finally block
    setIsSubmitting(false);
  }
};


  // Show loading state while configuration is loading
  if (isLoadingConfig) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h1>Request a Sub</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>Loading system configuration...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we load your school's settings.</div>
        </div>
      </div>
    );
  }

  // Show error if module is disabled
  if (error && error.includes('module is not enabled')) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
        <h1>Request a Sub</h1>
        <div style={{ 
          color: '#721c24', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          padding: '20px', 
          borderRadius: '8px', 
          marginTop: '20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚫</div>
          <h3 style={{ marginBottom: '16px' }}>Module Not Available</h3>
          <p style={{ fontSize: '16px', lineHeight: '1.5' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <h1>Request a Sub</h1>
      {canSubmitForOthers && (
        <div style={{ marginBottom: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={isSubmittingForOther}
              onChange={(e) => setIsSubmittingForOther(e.target.checked)}
              style={{ marginRight: '10px' }}
            />
            Submit request on behalf of another teacher?
          </label>
        </div>
      )}
      
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Basic Request Information */}
        <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>Request Details</h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="department-select"><strong>Department *</strong></label><br />
            <select
              id="department-select"
              name="department"
              value={formData.department}
              onChange={canSubmitForOthers && isSubmittingForOther ? handleDepartmentChangeForAdmin : handleChange}
              required
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
              disabled={isLoadingFaculty || (!canSubmitForOthers || (canSubmitForOthers && !isSubmittingForOther)) && !!(currentUser && allFacultyForTenant.find(f => f.email === currentUser.email)?.department)}
            >
              <option value="">Select department</option>
              {departments.map(dep => (<option key={dep} value={dep}>{dep}</option>))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="name-input-select"><strong>Name *</strong> (Teacher needing a sub)</label><br />
            {(canSubmitForOthers && isSubmittingForOther) ? (
              <select
                id="name-input-select"
                name="name" 
                value={selectedTeacherNameForAdmin} 
                onChange={handleChange} 
                required
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                disabled={isLoadingFaculty || !formData.department || teachersForAdminSelection.length === 0}
              >
                <option value="">Select name of absent teacher from selected department</option>
                {teachersForAdminSelection.map(fac => (
                  <option key={fac.id || fac.email} value={fac.name}>{fac.name}</option>
                ))}
              </select>
            ) : (
              <input
                id="name-input-select"
                type="text"
                name="name"
                value={formData.name} 
                readOnly
                required
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#e9ecef', cursor: 'default' }}
              />
            )}
          </div>

          {/* Enhanced date selection */}
          {renderDateSelection()}

          <div style={{ marginBottom: '15px' }}>
            <label><strong>Reason for Absence *</strong></label><br />
            <input type="text" name="reason" value={formData.reason} onChange={handleChange} required style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }} />
          </div>

          {/* Room section - only show for single day or unified multi-day */}
          {(requestMode === 'single' || (requestMode === 'multiple' && copySettingsToAll)) && (
            <div style={{ marginBottom: '15px' }}>
              <label><strong>Room Number</strong></label><br />
              
              {/* Multiple Rooms Checkbox */}
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={useMultipleRooms}
                    onChange={(e) => {
                      setUseMultipleRooms(e.target.checked);
                      if (!e.target.checked) {
                        // Clear period rooms when switching back to single room
                        setPeriodRooms({});
                        setFormData(prev => ({ ...prev, periodRooms: {} }));
                      } else {
                        // Clear main room when switching to multiple rooms
                        setFormData(prev => ({ ...prev, room: '' }));
                      }
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  <span>Different room for each period</span>
                </label>
              </div>

              {/* Single Room Input (when not using multiple rooms) */}
              {!useMultipleRooms && (
                <input 
                  type="text" 
                  name="room" 
                  value={formData.room} 
                  onChange={handleChange} 
                  placeholder={requestMode === 'multiple' ? "e.g., 203B or Lab A (applies to all dates)" : "e.g., 203B or Lab A"} 
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              )}

              {/* Multiple Rooms Notice */}
              {useMultipleRooms && (
                <div style={{ 
                  padding: '10px', 
                  backgroundColor: '#e7f3ff', 
                  border: '1px solid #b8daff', 
                  borderRadius: '4px',
                  fontSize: '14px',
                  color: '#0c5460'
                }}>
                  Room numbers will be specified for each selected period below.
                </div>
              )}
            </div>
          )}

          {/* Single day periods selection */}
          {requestMode === 'single' && (
            <div>
              <label><strong>Periods Missed *</strong></label><br />
              {!formData.date && (
                <div style={{ color: '#6c757d', fontStyle: 'italic', marginTop: '8px' }}>
                  Please select a date first to see available periods.
                </div>
              )}
              {formData.date && selectedDateScheduleInfo && (selectedDateScheduleInfo.source === 'holiday' || selectedDateScheduleInfo.source === 'weekend') && (
                <div style={{ 
                  color: '#721c24', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  marginTop: '8px' 
                }}>
                  {selectedDateScheduleInfo.source === 'holiday' 
                    ? `Cannot request substitute - this is a school holiday: ${selectedDateScheduleInfo.holidayInfo?.holidayName}` 
                    : 'Cannot request substitute - weekends are not school days.'}
                </div>
              )}
              {formData.date && availablePeriods.length === 0 && selectedDateScheduleInfo && 
               selectedDateScheduleInfo.source !== 'holiday' && selectedDateScheduleInfo.source !== 'weekend' && (
                <div style={{ 
                  color: '#721c24', 
                  backgroundColor: '#f8d7da', 
                  border: '1px solid #f5c6cb', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  marginTop: '8px' 
                }}>
                  No periods are available for this date. This may be a non-instructional day.
                </div>
              )}
              {formData.date && availablePeriods.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '8px' }}>
                  {availablePeriods.map(period => (
                    <label key={period.id} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '5px', 
                      padding: '8px 12px', 
                      border: '1px solid #dee2e6', 
                      borderRadius: '4px', 
                      backgroundColor: formData.periods.includes(period.id) ? '#e3f2fd' : 'white',
                      cursor: 'pointer'
                    }}>
                      <input 
                        type="checkbox" 
                        value={period.id} 
                        checked={formData.periods.includes(period.id)} 
                        onChange={handleCheckboxChange}
                      />
                      {period.name}
                      {period.start_time && period.end_time && (
                        <small style={{ color: '#6c757d', marginLeft: '4px' }}>
                          ({period.start_time} - {period.end_time})
                        </small>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom Fields Section - only show for single day or unified multi-day */}
        {tenantConfig.customFields && tenantConfig.customFields.length > 0 && 
         (requestMode === 'single' || (requestMode === 'multiple' && copySettingsToAll)) && (
          <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
              Additional Information
            </h3>
            
            {tenantConfig.customFields.map(field => (
              <div key={field.id} style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                  {field.name}{field.required ? ' *' : ''}
                  {field.sendNotification && (
                    <small style={{ 
                      color: '#28a745', 
                      marginLeft: '8px', 
                      fontSize: '12px',
                      fontWeight: 'normal'
                    }}>
                      Will notify: {field.notificationRecipientName || 'staff'}
                    </small>
                  )}
                </label>
                
                {field.type === 'text' && (
                  <input
                    type="text"
                    name={`customField_${field.id}`}
                    value={formData[`customField_${field.id}`] || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    required={field.required}
                    placeholder={field.description || `Enter ${field.name.toLowerCase()}`}
                  />
                )}
                
                {field.type === 'textarea' && (
                  <textarea
                    name={`customField_${field.id}`}
                    value={formData[`customField_${field.id}`] || ''}
                    onChange={handleChange}
                    rows={3}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    required={field.required}
                    placeholder={field.description || `Enter ${field.name.toLowerCase()}`}
                  />
                )}
                
                {field.type === 'select' && field.options && (
                  <select
                    name={`customField_${field.id}`}
                    value={formData[`customField_${field.id}`] || ''}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    required={field.required}
                  >
                    <option value="">Select {field.name.toLowerCase()}...</option>
                    {field.options.split('\n').filter(option => option.trim()).map(option => (
                      <option key={option.trim()} value={option.trim()}>{option.trim()}</option>
                    ))}
                  </select>
                )}
                
                {field.type === 'checkbox' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                    <input
                      type="checkbox"
                      name={`customField_${field.id}`}
                      checked={formData[`customField_${field.id}`] === 'true' || formData[`customField_${field.id}`] === true}
                      onChange={(e) => handleChange({
                        target: {
                          name: `customField_${field.id}`,
                          value: e.target.checked
                        }
                      })}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <span style={{ fontWeight: 'normal' }}>
                      {field.description || `Check if ${field.name.toLowerCase()} is needed`}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Multi-day periods section */}
        {renderMultipleDatePeriods()}

        {/* Period-specific details - only for single day or when periods are selected */}
        {((requestMode === 'single' && formData.periods.length > 0) || 
          (requestMode === 'multiple' && copySettingsToAll && formData.periods.length > 0)) && (
          <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>Period Details</h3>
            {formData.periods.sort((a,b) => {
              // Sort periods based on their order in availablePeriods
              const indexA = availablePeriods.findIndex(p => p.id === a);
              const indexB = availablePeriods.findIndex(p => p.id === b);
              
              if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
              }
              
              // Fallback to numeric sort
              return Number(a) - Number(b);
            }).map(p => {
              const periodConfig = availablePeriods.find(period => period.id === p);
              const periodName = periodConfig ? periodConfig.name : `Period ${p}`;
              const periodTime = periodConfig && periodConfig.start_time && periodConfig.end_time 
                ? `${periodConfig.start_time} - ${periodConfig.end_time}` 
                : '';

              return (
                <div key={`actions-for-period-${p}`} style={{ border: '1px solid #eee', padding: '15px', borderRadius: '5px', marginBottom: '15px', backgroundColor: '#f8f9fa' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>
                      {periodName} {periodTime && <small style={{ color: '#6c757d' }}>({periodTime})</small>}
                    </h4>
                    
                    {/* Room input for each period when useMultipleRooms is true */}
                    {useMultipleRooms && (
                      <div style={{ marginBottom: '15px' }}>
                        <label htmlFor={`room-${p}`}><strong>Room for {periodName}</strong></label><br />
                        <input 
                          type="text" 
                          id={`room-${p}`}
                          value={periodRooms[p] || ''}
                          onChange={(e) => handlePeriodRoomChange(p, e.target.value)}
                          placeholder="e.g., 203B, Lab A, Gym"
                          style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', marginTop: '5px' }}
                        />
                      </div>
                    )}
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor={`notes-${p}`}><strong>Notes for {periodName}</strong></label><br />
                        <textarea 
                          id={`notes-${p}`} 
                          rows={3} 
                          value={formData.periodNotes[p] || ''} 
                          onChange={(e) => handleNoteChange(p, e.target.value)} 
                          placeholder={`E.g., "Worksheet on desk", "Continue chapter 3"`} 
                          style={{ width: '100%', marginTop: '5px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor={`file-${p}`}><strong>Upload Primary File for {periodName}</strong> (Optional)</label><br />
                        <input 
                          type="file" 
                          id={`file-${p}`} 
                          accept="*" 
                          onChange={(e) => handleFileChange(p, e.target.files[0])} 
                          style={{ marginTop: '5px' }}
                        />
                        {periodFiles[p] && <span style={{ marginLeft: '10px', fontSize: '0.9em', color: 'green', fontWeight: 'bold' }}>✓ Selected: {periodFiles[p].name}</span>}
                    </div>

                    {/* Additional Files for this Period */}
                    <div style={{ marginTop: '15px', padding: '12px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                        <label htmlFor={`additional-files-${p}`} style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                            Upload Additional Files for {periodName} (Optional)
                        </label>
                        <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 8px 0' }}>
                            Add extra resources like answer keys, worksheets, or reference materials
                        </p>
                        <input
                            type="file"
                            id={`additional-files-${p}`}
                            multiple
                            accept="*"
                            onChange={(e) => handleAdditionalFilesChange(p, e.target.files)}
                            style={{
                                padding: '6px',
                                borderRadius: '4px',
                                border: '1px dashed #007bff',
                                backgroundColor: '#f8f9fa',
                                width: '100%',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}
                        />

                        {/* Display additional files for this period */}
                        {additionalPeriodFiles[p] && additionalPeriodFiles[p].length > 0 && (
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontWeight: 'bold', fontSize: '12px', color: '#495057', marginBottom: '5px', display: 'block' }}>
                                    Additional Files ({additionalPeriodFiles[p].length}):
                                </label>
                                <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                    {additionalPeriodFiles[p].map((file, index) => (
                                        <div
                                            key={`${file.name}-${index}`}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '6px 8px',
                                                marginBottom: '4px',
                                                backgroundColor: '#e3f2fd',
                                                borderRadius: '3px',
                                                fontSize: '12px'
                                            }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <span style={{ fontWeight: 'bold' }}>{file.name}</span>
                                                <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                                                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeAdditionalFile(p, index)}
                                                style={{
                                                    backgroundColor: '#dc3545',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '3px',
                                                    padding: '2px 6px',
                                                    cursor: 'pointer',
                                                    fontSize: '10px',
                                                    marginLeft: '8px'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Share with Substitutes */}
        <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', backgroundColor: 'white' }}>
          <h3 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>Share with Substitutes</h3>
          
          {/* Share with Departments */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>Share with Departments:</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
              {departments.map(dept => (
                <label key={dept} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: sharingOptions.shareWithDepartments.includes(dept) ? '#e8f5e8' : 'white', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={sharingOptions.shareWithDepartments.includes(dept)}
                    onChange={(e) => handleDepartmentSharingChange(dept, e.target.checked)}
                  />
                  {dept}
                </label>
              ))}
            </div>
          </div>

          {/* Share with Preferred Subs */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block' }}>
              <input
                type="checkbox"
                checked={sharingOptions.shareWithPreferredSubs}
                onChange={(e) => handleSharingChange('shareWithPreferredSubs', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Share with Preferred Substitutes
            </label>
            
            {sharingOptions.shareWithPreferredSubs && (
              <div style={{ marginTop: '15px', padding: '15px', border: '1px solid #e3f2fd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
                {/* Saved Groups Dropdown */}
                {savedGroups.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Quick Select from Saved Groups:</label>
                    <select
                      value={selectedGroup}
                      onChange={(e) => handleGroupSelection(e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="">Select a saved group...</option>
                      {savedGroups.map(group => (
                        <option key={group.id} value={group.id}>
                          {group.name} ({group.members?.length || 0} members)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quick Add Button */}
                <div style={{ marginBottom: '15px' }}>
                  <button
                    type="button"
                    onClick={() => setShowQuickAdd(!showQuickAdd)}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {showQuickAdd ? '− Hide' : '+ Add'} Individual Substitutes
                  </button>
                </div>

                {/* Individual Substitute Selection */}
                {showQuickAdd && (
                  <div style={{ marginBottom: '15px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '8px' }}>
                      {availableSubstitutes.map(sub => (
                        <label key={sub.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={sharingOptions.selectedPreferredSubs.some(s => s.id === sub.id)}
                            onChange={(e) => handlePreferredSubChange(sub, e.target.checked)}
                          />
                          <span style={{ fontSize: '14px' }}>{sub.name} ({sub.department})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Subs Display */}
                {sharingOptions.selectedPreferredSubs.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                      Selected Substitutes ({sharingOptions.selectedPreferredSubs.length}):
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {sharingOptions.selectedPreferredSubs.map(sub => (
                        <span
                          key={sub.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '4px 8px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '12px'
                          }}
                        >
                          {sub.name}
                          <button
                            type="button"
                            onClick={() => removePreferredSub(sub)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: 'white',
                              cursor: 'pointer',
                              padding: '0',
                              marginLeft: '4px',
                              fontSize: '14px',
                              fontWeight: 'bold'
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Save as Group Option */}
                {sharingOptions.selectedPreferredSubs.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={sharingOptions.saveAsGroup}
                        onChange={(e) => handleSharingChange('saveAsGroup', e.target.checked)}
                      />
                      <span style={{ fontWeight: 'bold' }}>Save this selection as a group for future use</span>
                    </label>
                    {sharingOptions.saveAsGroup && (
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Enter group name..."
                          value={sharingOptions.newGroupName}
                          onChange={(e) => handleSharingChange('newGroupName', e.target.value)}
                          style={{ flex: 1, padding: '6px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <button
                          type="button"
                          onClick={saveGroup}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Save Group
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Share with All Faculty */}
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: sharingOptions.shareWithAllFaculty ? '#ffe6e6' : 'white', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={sharingOptions.shareWithAllFaculty}
                onChange={(e) => handleSharingChange('shareWithAllFaculty', e.target.checked)}
              />
              <span style={{ fontWeight: 'bold' }}>Share with All Faculty</span>
              <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: 'auto' }}>
                (Will notify all {allFacultyForTenant.filter(f => f.email !== currentUser.email).length} faculty members)
              </span>
            </label>
          </div>

          {/* Sharing Summary */}
          {(sharingOptions.shareWithDepartments.length > 0 || sharingOptions.selectedPreferredSubs.length > 0 || sharingOptions.shareWithAllFaculty) && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '8px', border: '1px solid #bee5eb' }}>
              <h4 style={{ margin: '0 0 10px 0', color: '#0c5460' }}>Sharing Summary</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {sharingOptions.shareWithDepartments.length > 0 && (
                  <li>
                    <strong>Departments:</strong> {sharingOptions.shareWithDepartments.join(', ')} 
                    ({sharingOptions.shareWithDepartments.reduce((count, dept) => 
                      count + allFacultyForTenant.filter(f => (f.department === dept || f.secondaryDepartment === dept) && f.email !== currentUser.email).length, 0
                    )} people)
                  </li>
                )}
                {sharingOptions.selectedPreferredSubs.length > 0 && (
                  <li><strong>Preferred Substitutes:</strong> {sharingOptions.selectedPreferredSubs.length} selected</li>
                )}
                {sharingOptions.shareWithAllFaculty && (
                  <li><strong>All Faculty:</strong> {allFacultyForTenant.filter(f => f.email !== currentUser.email).length} people</li>
                )}
              </ul>
            </div>
          )}
        </div>
        
        {/* Feedback Section */}
        <div>
          {error && (
            <div style={{ 
              color: '#721c24', 
              backgroundColor: '#f8d7da', 
              border: '1px solid #f5c6cb', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '15px',
              fontWeight: 'bold'
            }}>
              {error}
            </div>
          )}
          {successMessage && (
            <div style={{ 
              color: '#155724', 
              backgroundColor: '#d4edda', 
              border: '1px solid #c3e6cb', 
              padding: '12px', 
              borderRadius: '8px', 
              marginBottom: '15px',
              fontWeight: 'bold'
            }}>
              {successMessage}
            </div>
          )}
        </div>

        <button 
          type="submit" 
          disabled={isSubmitting || 
                   (requestMode === 'single' && formData.date && (availablePeriods.length === 0 || 
                    (selectedDateScheduleInfo?.source === 'holiday' || selectedDateScheduleInfo?.source === 'weekend'))) || 
                   (requestMode === 'multiple' && selectedDates.length === 0)} 
          style={{
            backgroundColor: (isSubmitting || 
                           (requestMode === 'single' && formData.date && (availablePeriods.length === 0 || 
                            (selectedDateScheduleInfo?.source === 'holiday' || selectedDateScheduleInfo?.source === 'weekend'))) || 
                           (requestMode === 'multiple' && selectedDates.length === 0)) ? '#ccc' : '#007bff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px', 
            padding: '15px 30px', 
            fontWeight: 'bold', 
            fontSize: '16px',
            cursor: (isSubmitting || 
                    (requestMode === 'single' && formData.date && (availablePeriods.length === 0 || 
                     (selectedDateScheduleInfo?.source === 'holiday' || selectedDateScheduleInfo?.source === 'weekend'))) || 
                    (requestMode === 'multiple' && selectedDates.length === 0)) ? 'not-allowed' : 'pointer', 
            alignSelf: 'flex-start', 
            marginTop: '20px',
            transition: 'background-color 0.2s'
          }}
        >
          {isSubmitting ? 'Submitting...' : 
           requestMode === 'multiple' && selectedDates.length > 0 ? 
           `Submit ${selectedDates.length} Request${selectedDates.length > 1 ? 's' : ''}` : 
           'Submit Request'}
        </button>
      </form>
    </div>
  );
};

export default RequestSub;